import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { SetOwnership, sharesSumToOne, type Ownership } from '@syndiqia/registry/contracts';
import { errorMessage, useApi } from '../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useOwnership, useParties, useRegistryMutation, useUnits } from '../queries';
import { useSyndicate } from '../session';
import { useMyUnits } from '../comms-queries';
import { UnitAccount } from './finances/UnitAccount';

export function UnitPage() {
  const { t, number, date } = useI18n();
  const { unitId = '' } = useParams();
  const syndicate = useSyndicate();
  const units = useUnits();
  const ownership = useOwnership(unitId);
  const parties = useParties();
  const myUnits = useMyUnits('owned'); // the account is the owners'

  const unit = units.data?.find((u) => u.id === unitId);
  const partyName = (id: string) => parties.data?.find((p) => p.id === id)?.displayName ?? '…';

  if (units.isPending) return <Loading label={t.loading} />;
  if (!unit) return <Alert>{t.common.notFound}</Alert>;

  return (
    <>
      <Link to={`${syndicate.base}/units`} className="back">
        ← {t.nav.units}
      </Link>
      <PageHeader
        title={`${t.units.label} ${unit.label}`}
        intro={[
          t.units.kinds[unit.kind],
          unit.floor !== null ? `${t.units.floor} ${unit.floor}` : null,
          unit.areaM2 !== null ? `${number(unit.areaM2, 2)} m²` : null,
          unit.cadastralLot ? `${t.units.cadastralLot} ${unit.cadastralLot}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      />

      {syndicate.can('registry.contacts.read') && (
        <section>
          <h2>{t.ownership.current}</h2>
          {ownership.isPending && <Loading label={t.loading} />}
          {ownership.isError && <Alert>{errorMessage(ownership.error, t)}</Alert>}
          {ownership.data?.length === 0 && <Empty>{t.ownership.none}</Empty>}
          {!!ownership.data?.length && (
            <ul className="list">
              {ownership.data.map((o) => (
                <li key={o.partyId}>
                  <Link to={`${syndicate.base}/parties/${o.partyId}`}>
                    <strong>{partyName(o.partyId)}</strong>
                  </Link>{' '}
                  — {o.share.numerator}/{o.share.denominator} ·{' '}
                  <span className="muted">
                    {t.ownership.since} {date(o.from)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {syndicate.can('registry.ownership.write') && (
          <Disclosure label={t.ownership.record}>
            {(close) =>
              parties.data?.length ? (
                <OwnershipForm unitId={unit.id} current={ownership.data ?? []} onDone={close} />
              ) : (
                <Alert tone="info">{t.ownership.noParties}</Alert>
              )
            }
          </Disclosure>
          )}
        </section>
      )}
      {syndicate.can('registry.contacts.read') && <Occupants unitId={unit.id} />}
      {(syndicate.can('ledger.read') || myUnits.data?.includes(unit.id)) && <UnitAccount unitId={unit.id} />}
    </>
  );
}

interface OwnerRow {
  partyId: string;
  numerator: string;
  denominator: string;
}

function OwnershipForm({ unitId, current, onDone }: { unitId: string; current: Ownership[]; onDone: () => void }) {
  const { t } = useI18n();
  const parties = useParties();
  const myUnits = useMyUnits();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [rows, setRows] = useState<OwnerRow[]>([{ partyId: '', numerator: '1', denominator: '1' }]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useRegistryMutation<SetOwnership, Ownership[]>(
    (api, base, input) => api(`${base}/units/${unitId}/ownership`, { method: 'PUT', body: input }),
    ['ownership'],
  );

  const update = (i: number, patch: Partial<OwnerRow>) => setRows((r) => r.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const owners = rows.map((r) => ({
      partyId: r.partyId,
      share: { numerator: Number(r.numerator), denominator: Number(r.denominator) },
    }));
    const result = validate(SetOwnership, { effectiveFrom, owners });
    if (result.data && !sharesSumToOne(result.data.owners.map((o) => o.share))) {
      setErrors({ owners: t.ownership.sharesMustSumToOne });
      return;
    }
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <Field label={t.ownership.effectiveFrom} error={errors.effectiveFrom}>
        <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} min={current[0]?.from} />
      </Field>
      {rows.map((row, i) => (
        <div className="row" key={i}>
          <Field label={t.ownership.owner} error={errors[`owners.${i}.partyId`]}>
            <select value={row.partyId} onChange={(e) => update(i, { partyId: e.target.value })}>
              <option value="">—</option>
              {parties.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.ownership.share} error={errors[`owners.${i}.share`]}>
            <span className="fraction">
              <input value={row.numerator} onChange={(e) => update(i, { numerator: e.target.value })} inputMode="numeric" />
              /
              <input value={row.denominator} onChange={(e) => update(i, { denominator: e.target.value })} inputMode="numeric" />
            </span>
          </Field>
        </div>
      ))}
      <button
        type="button"
        className="link-button"
        onClick={() => setRows((r) => [...r, { partyId: '', numerator: '1', denominator: String(r.length + 1) }])}
      >
        + {t.ownership.addOwner}
      </button>
      {errors.owners && <Alert>{errors.owners}</Alert>}
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

/** Occupants (tenants) of a unit: they may submit requests and book, without seeing the owner's account. */
function Occupants({ unitId }: { unitId: string }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const m = t.members;
  const parties = useParties();
  const occupants = useQuery({
    queryKey: [s.tenantId, 'ownership', 'occupancy', unitId],
    queryFn: () => api<{ id: string; partyId: string; displayName: string; from: string; to: string | null }[]>(`${s.api}/registry/units/${unitId}/occupancy`),
  });
  const [values, setValues] = useState({ partyId: '', from: '' });
  const [endOn, setEndOn] = useState<Record<string, string>>({});
  const add = useRegistryMutation<{ partyId: string; from: string }, void>((a, base, input) => a(`${base}/units/${unitId}/occupancy`, { method: 'POST', body: input }), ['ownership', 'parties']);
  const end = useRegistryMutation<{ id: string; to: string }, void>((a, base, { id, to }) => a(`${base}/occupancy/${id}/end`, { method: 'POST', body: { to } }), ['ownership', 'parties']);
  const editable = s.can('registry.contacts.write');
  return (
    <section>
      <h2>{m.occupants}</h2>
      <p className="muted small">{m.occupantHint}</p>
      {occupants.data?.length === 0 && <Empty>{m.noOccupants}</Empty>}
      <ul className="list">
        {occupants.data?.map((o) => (
          <li key={o.id} className={o.to ? 'muted' : undefined}>
            <Link to={`${s.base}/parties/${o.partyId}`}>
              <strong>{o.displayName}</strong>
            </Link>{' '}
            · <span className="muted">{o.to ? m.period(date(o.from), date(o.to)) : m.since(date(o.from))}</span>
            {editable && !o.to && (
              <span className="inline-field">
                <input type="date" aria-label={m.endOn} value={endOn[o.id] ?? ''} onChange={(e) => setEndOn((v) => ({ ...v, [o.id]: e.target.value }))} />
                <button type="button" className="link-button small" disabled={!endOn[o.id] || end.isPending} onClick={() => end.mutate({ id: o.id, to: endOn[o.id]! })}>
                  {m.endOccupancy}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <Disclosure label={m.addOccupant}>
          {(close) => (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (values.partyId && values.from) add.mutate(values, { onSuccess: () => (setValues({ partyId: '', from: '' }), close()) });
              }}
            >
              <Field label={m.person}>
                <select value={values.partyId} onChange={(e) => setValues((v) => ({ ...v, partyId: e.target.value }))}>
                  <option value="">—</option>
                  {parties.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={m.from}>
                <input type="date" value={values.from} onChange={(e) => setValues((v) => ({ ...v, from: e.target.value }))} />
              </Field>
              <div className="form-actions">
                <button type="submit" className="button button-primary" disabled={add.isPending || !values.partyId || !values.from}>
                  {t.common.save}
                </button>
              </div>
            </form>
          )}
        </Disclosure>
      )}
      {(add.isError || end.isError) && <Alert>{errorMessage(add.error ?? end.error, t)}</Alert>}
    </section>
  );
}
